import React from 'react';
import {
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesIconButton,
  AgnesPageHeader,
} from '@agnes/ui';
import { Download, FolderOpen } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import ServiceESocial from '@/services/ServiceESocial';
import { notify } from '@/stores/useToast';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

interface Exportacao {
  [k: string]: unknown;
  id: number;
  documento: string;
  modulo: string;
  solicitacao: string;
  processamento: string;
  status: 'Concluído' | 'Aguardando' | 'Erro';
}

/** Mapeia o status cru do BFF para o rótulo exibido na tabela. */
function mapStatus(s: unknown): Exportacao['status'] {
  const u = String(s ?? '').toUpperCase();
  if (u.includes('CONC') || u.includes('DONE') || u.includes('COMPLET')) return 'Concluído';
  if (u.includes('ERRO') || u.includes('FAIL')) return 'Erro';
  return 'Aguardando';
}

function fmtData(iso: unknown): string {
  if (!iso) return '-';
  const d = new Date(iso as string);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('pt-BR');
}

// CON-5204: cada coluna da tabela mapeia para o alias de ordenação aceito pelo backend
// (GET report-process?sort=<alias>,<asc|desc>).
const SORT_ALIASES: Record<string, string> = {
  documento: 'name',
  modulo: 'module',
  solicitacao: 'startProcess',
  processamento: 'endProcess',
  status: 'status',
};

export default function PageDownloads() {
  const [rows, setRows] = React.useState<Exportacao[]>([]);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(50);
  // CON-5204: todas as colunas são ordenáveis (server-side). Ao entrar, a listagem abre
  // por Solicitação decrescente (mais recente no topo) — a seta já aparece nessa coluna.
  const [sortBy, setSortBy] = React.useState('solicitacao');
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');

  const carregar = React.useCallback(async () => {
    const alias = SORT_ALIASES[sortBy] ?? 'startProcess';
    const { data } = await ServiceESocial.report.getAll(`${alias},${sortOrder}`);
    const lista: Exportacao[] = (Array.isArray(data) ? data : []).map(
      (item: Record<string, unknown>) => ({
        id: item.id as number,
        documento: (item.name as string) ?? '',
        modulo: (item.module as string) ?? '-',
        solicitacao: fmtData(item.startProcess),
        processamento: fmtData(item.endProcess),
        status: mapStatus(item.status),
      }),
    );
    return lista;
  }, [sortBy, sortOrder]);

  React.useEffect(() => {
    let ativo = true;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const lista = await carregar();
        if (!ativo) return;
        setRows(lista);
        if (lista.some((r) => r.status === 'Aguardando')) {
          timer = setTimeout(tick, 4000);
        }
      } catch {
        if (ativo) notify.error('Erro ao buscar exportações.');
      }
    };
    tick();
    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [carregar]);

  async function baixar(id: number, nome: string) {
    try {
      const resp = await ServiceESocial.report.download(id);
      // A extensão segue o tipo real do arquivo (o BFF pode devolver PDF ou XLSX). O
      // responseType:'blob' já traz o content-type no Blob; cair pro header e, por fim,
      // xlsx (compatibilidade com os exports que continuam em planilha).
      const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data]);
      const contentType = String(blob.type || resp.headers?.['content-type'] || '');
      const ext = contentType.includes('pdf') ? 'pdf' : 'xlsx';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${nome || 'export'}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      notify.error('Erro ao baixar exportação.');
    }
  }

  // A ordem vem do backend (server-side sort via report-process?sort=). Não reordenar aqui.
  const data = rows;

  const columns: AgnesDataTableColumn<Exportacao>[] = [
    { key: 'documento', header: 'Documento', width: 420, sortable: true },
    { key: 'modulo', header: 'Módulo', width: 220, sortable: true },
    { key: 'solicitacao', header: 'Solicitação', width: 180, sortable: true },
    { key: 'processamento', header: 'Processamento', width: 180, sortable: true },
    { key: 'status', header: 'Status', width: 130, sortable: true },
    {
      key: 'acoes',
      header: 'Ações',
      width: 90,
      align: 'center',
      render: (r) => (
        <AgnesIconButton
          aria-label="Download"
          size="sm"
          disabled={r.status !== 'Concluído'}
          onClick={() => baixar(r.id, r.documento)}
        >
          <Download className="h-4 w-4" />
        </AgnesIconButton>
      ),
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<FolderOpen />}
        title="Exportações"
        subtitle="Acesse os arquivos extraídos mediante botões de exportação disponíveis nos módulos"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Arquivos' },
          { label: 'Exportações' },
        ]}
      />

      <div className="mt-2 rounded-2xl border border-agnes-border bg-agnes-bg-surface p-4">
        {data.length === 0 ? (
          <EmptyState
            icon={<Download className="h-7 w-7" />}
            title="Nenhuma exportação disponível"
          />
        ) : (
          <TableWrapper>
            <AgnesDataTable<Exportacao>
              columns={columns}
              data={data}
              rowKey={(r) => String(r.id)}
              sort={{
                sortBy,
                sortOrder,
                onSortChange: (by, order) => {
                  setSortBy(by);
                  setSortOrder(order);
                  setPage(0);
                },
              }}
              pagination={{
                page,
                pageSize,
                total: data.length,
                onPageChange: setPage,
                onPageSizeChange: (s) => {
                  setPageSize(s);
                  setPage(0);
                },
              }}
            />
          </TableWrapper>
        )}
      </div>
    </>
  );
}
